export type AuthDialogResult =
  | { type: 'closed' }
  | { data: unknown; origin: string; type: 'message' };

const trustedOriginPattern = /^https:\/\/([a-z0-9-]+\.)*lucioai\.com$/i;
const localhostOriginPattern = /^https?:\/\/localhost(:\d+)?$/i;

function isTrustedOrigin(origin: string): boolean {
  if (origin === window.location.origin) {
    return true;
  }
  if (trustedOriginPattern.test(origin)) {
    return true;
  }
  return import.meta.env.DEV && localhostOriginPattern.test(origin);
}

/**
 * Opens a popup window to an auth URL and awaits a structured message.
 * The popup page must send its result via `window.opener.postMessage(...)`;
 * messages are only accepted from the popup window itself AND from a trusted
 * origin (this origin, *.lucioai.com, or localhost in dev).
 *
 * @param url - Absolute or same-origin relative URL of the popup page (must use HTTPS)
 * @param options - Optional configuration object
 * @param options.width - Width percentage of screen (default: 50)
 * @param options.height - Height percentage of screen (default: 50)
 * @returns Promise that resolves with the popup message, or `{ type: 'closed' }` if the
 *          user closed the popup, or rejects if the popup could not be opened
 *          (e.g. blocked by the browser)
 */
export async function openAuthDialogAndWaitForMessage(
  url: string,
  options: { height?: number; width?: number } = {},
): Promise<AuthDialogResult> {
  const { height = 50, width = 50 } = options;

  const popupWidth = Math.round((width / 100) * screen.width);
  const popupHeight = Math.round((height / 100) * screen.height);
  const left = Math.round((screen.width - popupWidth) / 2);
  const top = Math.round((screen.height - popupHeight) / 2);

  const features = `width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`;

  return new Promise((resolve, reject) => {
    const popup = window.open(url, '_blank', features);

    if (!popup) {
      reject(
        new Error(
          'The browser blocked the popup window. Please allow popups for this site and try again.',
        ),
      );
      return;
    }

    let settled = false;

    const cleanup = () => {
      window.removeEventListener('message', onMessage);
      clearInterval(closedPollInterval);
    };

    const settle = (fn: () => void) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanup();
      fn();
    };

    // Listen for postMessage from the popup
    const onMessage = (event: MessageEvent) => {
      // Only accept messages sent by the window we opened, from a trusted origin
      if (event.source !== popup || !isTrustedOrigin(event.origin)) {
        return;
      }

      const result: AuthDialogResult = { data: event.data, origin: event.origin, type: 'message' };

      if (!popup.closed) {
        popup.close();
      }

      settle(() => resolve(result));
    };

    window.addEventListener('message', onMessage);

    // Poll for user closing the popup manually
    const closedPollInterval = setInterval(() => {
      if (popup.closed) {
        settle(() => resolve({ type: 'closed' }));
      }
    }, 500);
  });
}
